import Background from '@/components/Background/Background'
import Logo from '@/components/Logo/Logo'
import Team from '@/components/Team/Team'
import Trapezoid from '@/components/Trapezoid/Trapezoid'
import { SPORT_ID } from '@/config/sports'
import { useTheme } from '@/contexts/ThemeContext'
import { useResolution } from '@/hooks/useResolution'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import './IntroTemplate.css'

import type { JsonMatchData } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface IntroTemplateProps {
  matchData?: MatchData
  customerId: string
  eventGuid: string
  jsonData: JsonMatchData | null
  isVisible?: boolean
  sportId?: number
}

const IntroTemplate: FC<IntroTemplateProps> = ({ matchData, jsonData, isVisible = true, sportId }) => {
  const matchConfig = matchData || {}
  const theme = useTheme()
  const resolution = useResolution()
  const animationRef = useTemplateAnimation(isVisible, { animationType: 'slideUp', duration: 0.5 })

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  // Baseball convention: away team on left, home team on right
  const awayFirst = sportId === SPORT_ID.BASEBALL

  // Get event info from JSON data
  const eventDescription = jsonData?.eventDescription || 'Stream starts soon'
  const eventStartTime = jsonData?.eventStartTime ? new Date(jsonData.eventStartTime) : null

  return (
    <Background>
      <div className="intro-container">
        {/* Top section with logos - v1 style positioning */}
        <div className="intro-logo-container">
          <Logo matchConfig={matchConfig} slotId={1} teamName="Watermark 1" />
          <Logo matchConfig={matchConfig} slotId={2} teamName="Watermark 2" />
        </div>

        {/* Spacer to push content down from logos */}
        <div />

        <div ref={animationRef} className="intro-content-wrapper">
          <div
            className="intro-card"
            style={{
              width: resolution.height,
            }}
            data-intro-panel
          >
            {/* FrogBox branding logo */}
            <Trapezoid
              style={{
                background: hexToRgba(theme.colors.primary, 0.95),
                width: Math.round(resolution.height * 0.3),
              }}
            >
              <img
                src="/assets/frogbox-logo-mono.png"
                alt="FrogBox"
                style={{ height: `calc(${theme.baseHeight} * 0.5` }}
              />
            </Trapezoid>

            <div className="intro-card-content">
              <div
                className="intro-main"
                style={{
                  background: hexToRgba(theme.colors.primary, 0.95),
                  minHeight: `calc(${theme.baseHeight} * 1.667)`,
                }}
              >
                <Team data={awayFirst ? awayCompetitor : homeCompetitor} style={{ width: '40%' }} isHome={!awayFirst} />
                <p className="intro-main-vs" style={{ fontSize: `calc(${theme.baseHeight} * 0.8)` }}>
                  {awayFirst ? '@' : 'vs.'}
                </p>
                <Team
                  data={awayFirst ? homeCompetitor : awayCompetitor}
                  style={{ width: '40%' }}
                  isHome={awayFirst}
                  align="right"
                />
              </div>

              <div
                className="intro-footer"
                style={{
                  background: hexToRgba(theme.colors.secondary, 0.95),
                  minHeight: `calc(${theme.baseHeight} * 1.333)`,
                  paddingBottom: `calc(${theme.baseHeight} / 16)`,
                  fontSize: `calc(${theme.baseHeight} * 0.583)`,
                }}
              >
                <p style={{ width: eventStartTime ? '48%' : '100%' }}>{eventDescription}</p>
                {eventStartTime && (
                  <p
                    style={{ width: '48%' }}
                  >{`${eventStartTime.toDateString()}, ${eventStartTime.toLocaleTimeString('en-AU')}`}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Background>
  )
}

export default IntroTemplate
